import React, { useEffect, useState } from "react";
import { Country } from "../utils/countryUtils";
import { parsePhoneNumber, AsYouType, type PhoneNumber } from "libphonenumber-js";

interface PhoneNumberInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onValidationChange?: (isValid: boolean) => void;
  name?: string;
  selectedCountry: Country;
  id?: string;
  className?: string;
  placeholder?: string;
}

export function PhoneNumberInput({
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  onValidationChange,
  name,
  selectedCountry,
  id = "phoneNumber",
  className = "",
  placeholder,
}: PhoneNumberInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value === undefined) return;
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!trimmed) {
      onValidationChange?.(false);
      return;
    }
    const { isValid } = parsePhoneNumberInternal(trimmed);
    onValidationChange?.(isValid);
  }, []);

  // Get country code for libphonenumber-js
  const getCountryCode = (country: Country): "US" | "CA" => {
    return country.code === "US" || country.code === "CA" ? country.code as "US" | "CA" : "US";
  };

  // Parse and validate phone number
  const parsePhoneNumberInternal = (input: string): { number: PhoneNumber | null; isValid: boolean } => {
    if (!input.trim()) return { number: null, isValid: false };
    
    try {
      const countryCode = getCountryCode(selectedCountry);
      const number = parsePhoneNumber(input, countryCode);
      const isValid = number ? number.isValid() : false;
      return { number, isValid };
    } catch {
      return { number: null, isValid: false };
    }
  };

  // Get placeholder text
  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    return selectedCountry.code === "CA" ? "(416) 555-1234" : "(555) 123-4567";
  };

  // Format display value
  const getDisplayValue = () => {
    if (!value) return "";
    
    const { number, isValid } = parsePhoneNumberInternal(value);
    if (isValid && number) {
      return number.formatNational();
    }
    return value;
  };

  // Validate and show errors
  const validateInput = (input: string, showError: boolean = true, respectFocus: boolean = true) => {
    if (respectFocus && isFocused) {
      return true;
    }

    if (!input.trim()) {
      onValidationChange?.(false);
      return false;
    }

    const { isValid } = parsePhoneNumberInternal(input);
    if (!isValid && showError) {
    } else if (isValid) {
    }
    
    onValidationChange?.(isValid);
    return isValid;
  };

  // Format input as user types
  const formatAsYouType = (input: string): string => {
    if (!input) return input;
    
    // If the original input starts with +, don't try to format further (it's international)
    // But if this is coming from getDisplayValue(), it should already be in national format
    if (input.startsWith('+')) return input;
    
    try {
      const countryCode = getCountryCode(selectedCountry);
      const formatter = new AsYouType(countryCode);
      const digitsOnly = input.replace(/\D/g, '');
      
      let formatted = '';
      for (const digit of digitsOnly) {
        formatted = formatter.input(digit);
      }
      return formatted;
    } catch {
      return input;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/[^0-9\s+\-()]/g, '');
    const { number, isValid } = parsePhoneNumberInternal(input);
    
    // Send E.164 format if valid, otherwise send raw input
    if (isValid && number) {
      onChange(number.format('E.164'));
    } else {
      onChange(input);
    }
    
    // While typing, notify parent of validation for non-empty input
    // This enables immediate positive feedback and submit enablement when valid
    if (input.trim()) {
      onValidationChange?.(isValid);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const controlKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    const isControlAction = (e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase());
    const isValidChar = /[0-9\s+\-()]/.test(e.key);
    
    if (!controlKeys.includes(e.key) && !isControlAction && !isValidChar) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/[^0-9\s+\-()]/g, '');
    const input = e.currentTarget;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = input.value.substring(0, start) + pastedText + input.value.substring(end);
    
    handleInputChange({ target: { value: newValue } } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    validateInput(value, true, false); // Don't respect focus state when explicitly validating on blur
    onBlur?.();
  };


  return (
    <div className={`w-full ${className}`}>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative mt-1">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <div className="flex items-center h-full pr-2 text-sm">
            <span className="text-base mr-1">{selectedCountry.flag}</span>
          </div>
        </div>

        <input
          id={id}
          name={name}
          value={formatAsYouType(getDisplayValue())}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type="tel"
          className={`w-full bg-transparent placeholder:text-slate-400 text-slate-700 text-sm border rounded-md shadow-sm py-2 pl-[3rem] pr-10 focus:outline-none focus:ring-1 focus:ring-offset-0 transition-colors duration-200 ${
            value
              ? (parsePhoneNumberInternal(value).isValid
                  ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                  : (isFocused
                      ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      : 'border-red-500 focus:border-red-500 focus:ring-red-500'))
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          placeholder={getPlaceholder()}
          maxLength={18}
          aria-describedby={ undefined}
        />

        {/* Validation indicator */}
        {value && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            {parsePhoneNumberInternal(value).isValid ? (
              <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              !isFocused && (
                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )
            )}
          </div>
        )}
      </div>

    </div>
  );
}
